// LeavePolicyUIService - Handles dynamic UI configuration and validation for different leave types

export interface UIFieldConfig {
  visible: boolean;
  required: boolean;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  helpText?: string;
}

export interface LeavePolicyUIConfig {
  totalPerYear: UIFieldConfig;
  maxDaysPerRequest: UIFieldConfig;
  minAdvanceNotice: UIFieldConfig;
  allowEmergency: UIFieldConfig;
  autoApprove: UIFieldConfig;
  requiresApproval: UIFieldConfig;
  isPaid: UIFieldConfig;
  isProRated: UIFieldConfig;
  allowCarry: UIFieldConfig;
  carryLimit: UIFieldConfig;
  creditFrequency: UIFieldConfig;
  maxBalance: UIFieldConfig;
  encashable: UIFieldConfig;
}

// Leave type specific configurations based on policy requirements
const LEAVE_TYPE_CONFIGS: Record<string, LeavePolicyUIConfig> = {
  'Short Leave': {
    totalPerYear: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable - Short leave is not an entitlement but allowed once per month'
    },
    maxDaysPerRequest: { 
      visible: false, 
      required: false, 
      helpText: 'Fixed duration: 2 hours (0.25 days) enforced by policy'
    },
    minAdvanceNotice: { 
      visible: true, 
      required: true,
      min: 0,
      helpText: 'Prior approval required from immediate manager before taking leave'
    },
    allowEmergency: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable - short leave requires prior approval'
    },
    autoApprove: { 
      visible: true, 
      required: false,
      disabled: true,
      helpText: 'Always requires manager approval - cannot be auto-approved'
    },
    requiresApproval: { 
      visible: true, 
      required: true,
      disabled: true,
      helpText: 'Always requires prior approval from immediate manager'
    },
    isPaid: { 
      visible: true, 
      required: true,
      disabled: true,
      helpText: 'Always paid leave'
    },
    isProRated: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable - not an entitlement'
    },
    allowCarry: { 
      visible: false, 
      required: false,
      disabled: true,
      helpText: 'Not applicable - not an entitlement'
    },
    carryLimit: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable'
    },
    creditFrequency: { visible: false, required: false },
    maxBalance: { visible: false, required: false },
    encashable: { visible: false, required: false },
  },
  
  'Casual Leave': {
    totalPerYear: { 
      visible: true, 
      required: true,
      helpText: 'Total days per calendar year (pro-rata optional)'
    },
    maxDaysPerRequest: { 
      visible: true, 
      required: false,
      step: 0.5,
      helpText: 'Optional per-request limit'
    },
    minAdvanceNotice: { 
      visible: true, 
      required: false,
      helpText: 'Advance notice in hours (optional)'
    },
    allowEmergency: { 
      visible: true, 
      required: false,
      helpText: 'Allow emergency situations with supporting proof'
    },
    autoApprove: { 
      visible: true, 
      required: false,
      helpText: 'Subject to approval process as per company hierarchy'
    },
    requiresApproval: { 
      visible: true, 
      required: false,
      helpText: 'Requires approval from reporting manager'
    },
    isPaid: { 
      visible: true, 
      required: false,
      helpText: 'Mark as paid or unpaid'
    },
    isProRated: { 
      visible: true, 
      required: false,
      helpText: 'Calculate on pro-rata basis if applicable'
    },
    allowCarry: { 
      visible: true, 
      required: false,
      helpText: 'Allow carry forward if desired'
    },
    carryLimit: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable - cannot be carried forward'
    },
    creditFrequency: { visible: false, required: false },
    maxBalance: { visible: false, required: false },
    encashable: { visible: false, required: false },
  },
  
  'Earned Leave': {
    totalPerYear: { 
      visible: true, 
      required: true,
      helpText: 'Days per year (eligibility rules separately)'
    },
    maxDaysPerRequest: { 
      visible: true, 
      required: false,
      helpText: 'Optional per-request minimum/maximum as per policy'
    },
    minAdvanceNotice: { 
      visible: true, 
      required: false,
      helpText: 'Advance notice in hours (optional)'
    },
    allowEmergency: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable for earned leave'
    },
    autoApprove: { 
      visible: true, 
      required: false,
      helpText: 'Subject to approval process as per company hierarchy'
    },
    requiresApproval: { 
      visible: true, 
      required: true,
      helpText: 'Requires approval from reporting manager'
    },
    isPaid: { 
      visible: true, 
      required: false,
      helpText: 'Mark as paid or unpaid'
    },
    isProRated: { 
      visible: true, 
      required: false,
      helpText: 'Enable pro-rata credit if desired'
    },
    allowCarry: { 
      visible: true, 
      required: false,
      helpText: 'Allow carry forward if desired'
    },
    carryLimit: { 
      visible: true, 
      required: false,
      helpText: 'Maximum carry forward days (optional)'
    },
    creditFrequency: { visible: false, required: false },
    maxBalance: { visible: false, required: false },
    encashable: { visible: true, required: false, helpText: 'If enabled, encashment will be available per company rules' },
  },
  
  'Compensatory Off': {
    totalPerYear: { 
      visible: true, 
      required: false,
      helpText: 'Optional: enter an annual cap if your policy requires it'
    },
    maxDaysPerRequest: { 
      visible: true, 
      required: false,
      helpText: 'Optional per-request limit'
    },
    minAdvanceNotice: { 
      visible: true, 
      required: false, // Changed from true to false - Comp-Off can be applied as needed
      min: 0,
      helpText: 'Can be applied as needed after working on holiday'
    },
    allowEmergency: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable for compensatory off'
    },
    autoApprove: { 
      visible: true, 
      required: false,
      helpText: 'Subject to verification by Attendance Register'
    },
    requiresApproval: { 
      visible: true, 
      required: true,
      helpText: 'Eligible only if worked on holiday with prior approval (minimum 6 billable hours)'
    },
    isPaid: { 
      visible: true, 
      required: false,
      helpText: 'Mark as paid or unpaid'
    },
    isProRated: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable - based on actual holiday work'
    },
    allowCarry: { 
      visible: true, 
      required: false,
      helpText: 'Allow carry forward if desired'
    },
    carryLimit: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable - must use within 1 month'
    },
    creditFrequency: { visible: false, required: false },
    maxBalance: { visible: true, required: false, helpText: 'Optional cap on total comp-off balance' },
    encashable: { visible: false, required: false },
  },
  
  'Leave without Pay': {
    totalPerYear: { 
      visible: true, 
      required: false,
      helpText: 'No fixed limit - applied when other leave balances are exhausted'
    },
    maxDaysPerRequest: { 
      visible: true, 
      required: false,
      helpText: 'No specific limit but affects salary and benefits calculation'
    },
    minAdvanceNotice: { 
      visible: true, 
      required: false,
      helpText: 'Advance notice in hours (optional)'
    },
    allowEmergency: { 
      visible: true, 
      required: false,
      helpText: 'Can be applied in emergency situations with proper documentation'
    },
    autoApprove: { visible: true, required: false },
    requiresApproval: { visible: true, required: false },
    isPaid: { visible: true, required: false },
    isProRated: { 
      visible: false, 
      required: false,
      disabled: true,
      helpText: 'Not applicable - LWP is not an entitlement'
    },
    allowCarry: { 
      visible: false, 
      required: false,
      disabled: true,
      helpText: 'Not applicable - LWP cannot be carried forward'
    },
    carryLimit: { 
      visible: false, 
      required: false,
      helpText: 'Not applicable'
    },
    creditFrequency: { visible: false, required: false },
    maxBalance: { visible: false, required: false },
    encashable: { visible: false, required: false },
  }
};

// Default configuration for new/unknown leave types
const DEFAULT_CONFIG: LeavePolicyUIConfig = {
  totalPerYear: { visible: true, required: true },
  maxDaysPerRequest: { visible: true, required: true },
  minAdvanceNotice: { visible: true, required: false },
  allowEmergency: { visible: true, required: false },
  autoApprove: { visible: true, required: false },
  requiresApproval: { visible: true, required: false },
  isPaid: { visible: true, required: false },
  isProRated: { visible: true, required: false },
  allowCarry: { visible: true, required: false },
  carryLimit: { visible: true, required: false },
  creditFrequency: { visible: true, required: false },
  maxBalance: { visible: true, required: false },
  encashable: { visible: true, required: false },
};

export const LeavePolicyUIService = {
   // Get UI configuration for a specific leave type

  getUIConfigForLeaveType: (leaveTypeName: string): LeavePolicyUIConfig => {
    return LEAVE_TYPE_CONFIGS[leaveTypeName] || DEFAULT_CONFIG;
  },

   // Apply default values based on leave type

  applyUIDefaults: (leaveTypeName: string, data: any) => {
    const config = LEAVE_TYPE_CONFIGS[leaveTypeName];
    if (!config) return data;

    const defaults: any = { ...data };

    // Apply leave type specific defaults
    switch (leaveTypeName) {
      case 'Casual Leave':
        defaults.totalPerYear = defaults.totalPerYear || '8';
        defaults.maxDaysPerRequest = defaults.maxDaysPerRequest || '3';
        defaults.minAdvanceNotice = defaults.minAdvanceNotice || '48';
        defaults.isPaid = 'true';
        defaults.isProRated = 'true';
        defaults.allowCarry = 'false';
        break;
        
      case 'Earned Leave':
        defaults.totalPerYear = defaults.totalPerYear || '14';
        defaults.maxDaysPerRequest = defaults.maxDaysPerRequest || '';
        defaults.minAdvanceNotice = defaults.minAdvanceNotice || '168';
        defaults.isPaid = 'true';
        defaults.isProRated = 'true';
        defaults.allowCarry = 'true';
        defaults.carryLimit = defaults.carryLimit || '5';
        break;
        
      case 'Short Leave':
        defaults.requiresApproval = 'true';
        defaults.isPaid = 'true';
        defaults.allowCarry = 'false';
        break;
        
      case 'Compensatory Off':
        defaults.maxDaysPerRequest = defaults.maxDaysPerRequest || '1';
        defaults.isPaid = 'true';
        defaults.allowCarry = 'false';
        break;
        
      case 'Leave without Pay':
        defaults.isPaid = 'false';
        defaults.isProRated = 'false';
        defaults.allowCarry = 'false';
        defaults.requiresApproval = 'true';
        defaults.autoApprove = 'false';
        break;
    }

    return defaults;
  },

   // Validate field value based on leave type and field configuration

  validateFieldValue: (
    leaveTypeName: string,
    fieldName: keyof LeavePolicyUIConfig,
    value: any
  ): { isValid: boolean; message?: string } => {
    const config = LeavePolicyUIService.getUIConfigForLeaveType(leaveTypeName);
    const fieldConfig = config[fieldName];

    if (!fieldConfig.visible) {
      return { isValid: true };
    }

    if (fieldConfig.required && (value === undefined || value === null || value === '')) {
      return { isValid: false, message: 'This field is required.' };
    }

    const numValue = Number(value);
    
    if (fieldConfig.min !== undefined && numValue < fieldConfig.min) {
      return { isValid: false, message: `Value must be at least ${fieldConfig.min}` };
    }
    
    if (fieldConfig.max !== undefined && numValue > fieldConfig.max) {
      return { isValid: false, message: `Value must not exceed ${fieldConfig.max}` };
    }

    // Strict policy enforcement based on leave type
    return LeavePolicyUIService.validatePolicyCompliance(leaveTypeName, fieldName, value);
  },

   // Validate complete form data against policy requirements

  validateCompletePolicy: (leaveTypeName: string, formData: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Get the config for this leave type (with fallback to DEFAULT_CONFIG)
    const config = LeavePolicyUIService.getUIConfigForLeaveType(leaveTypeName);
    
    // Validate each field
    Object.keys(formData).forEach(fieldName => {
      if (fieldName in config) {
        const validation = LeavePolicyUIService.validateFieldValue(
          leaveTypeName, 
          fieldName as keyof LeavePolicyUIConfig, 
          formData[fieldName]
        );
        if (!validation.isValid && validation.message) {
          errors.push(`${fieldName}: ${validation.message}`);
        }
      }
    });

    // Additional policy-specific validations
    const policyValidation = LeavePolicyUIService.validateFullPolicyCompliance(leaveTypeName, formData);
    if (!policyValidation.isValid) {
      errors.push(...policyValidation.errors);
    }

    return { isValid: errors.length === 0, errors };
  },

   // Strict policy compliance validation for individual fields

  validatePolicyCompliance: (
    leaveTypeName: string,
    fieldName: keyof LeavePolicyUIConfig,
    value: any
  ): { isValid: boolean; message?: string } => {
    // Relaxed: business rules enforced by admins; only basic per-field constraints apply
    return { isValid: true };
  },

   // Validate complete policy compliance

  validateFullPolicyCompliance: (leaveTypeName: string, formData: any): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Relaxed: no strong cross-field business constraints at this layer

    return { isValid: errors.length === 0, errors };
  },

   // Get all predefined leave types

  getPredefinedLeaveTypes: () => {
    return ['Short Leave', 'Casual Leave', 'Earned Leave', 'Compensatory Off', 'Leave without Pay'];
  },

   // Get leave type policy summary for display

  getLeavePolicySummary: (leaveTypeName: string) => {
    const summaries: Record<string, string> = {
      'Short Leave': 'Allowed once per month, max 2 hours, requires prior manager approval',
      'Casual Leave': '8 days per year (pro-rata), max 3 days per spell, includes emergency situations with supporting proof when required, cannot carry forward',
      'Earned Leave': '14 days per year after 1 year service, 7 days notice, max 5 days carry forward allowed',
      'Compensatory Off': 'For holiday work with prior approval, 6+ billable hours required, use within 1 month',
      'Leave without Pay': 'Unpaid leave when other balances exhausted, affects salary, requires management approval'
    };
    
    return summaries[leaveTypeName] || 'Custom leave type - configure as needed';
  }
};

// Pro-rata calculation tables for reference
export const CASUAL_LEAVE_PRORATE_TABLE = [
  { month: 'January (1-15)', entitlement: 8 },
  { month: 'January (16-31)', entitlement: 7 },
  { month: 'February', entitlement: 6 },
  { month: 'March', entitlement: 5.5 },
  { month: 'April', entitlement: 5 },
  { month: 'May', entitlement: 4.5 },
  { month: 'June', entitlement: 4 },
  { month: 'July', entitlement: 3.5 },
  { month: 'August', entitlement: 3 },
  { month: 'September', entitlement: 2.5 },
  { month: 'October', entitlement: 2 },
  { month: 'November', entitlement: 1.5 },
  { month: 'December (1-15)', entitlement: 1 },
  { month: 'December (16-31)', entitlement: 0 }
];

export const EARNED_LEAVE_PRORATE_TABLE = [
  { month: 'January (1-15)', entitlement: 12 },
  { month: 'January (16-31)', entitlement: 11.5 },
  { month: 'February', entitlement: 11 },
  { month: 'March', entitlement: 10 },
  { month: 'April', entitlement: 9 },
  { month: 'May', entitlement: 8 },
  { month: 'June', entitlement: 7 },
  { month: 'July', entitlement: 6 },
  { month: 'August', entitlement: 5 },
  { month: 'September', entitlement: 4 },
  { month: 'October', entitlement: 3 },
  { month: 'November', entitlement: 2 },
  { month: 'December (1-15)', entitlement: 1 },
  { month: 'December (16-31)', entitlement: 0 }
];

export const LWP_EL_REDUCTION_TABLE = [
  { lwpDays: 'Up to 7 days', elReduction: 0 },
  { lwpDays: '8 to 14 days', elReduction: 1 },
  { lwpDays: '15-22 days', elReduction: 2 },
  { lwpDays: '23-29 days', elReduction: 3 },
  { lwpDays: '30-37 days', elReduction: 4 },
  { lwpDays: '38-45 days', elReduction: 5 },
  { lwpDays: '46-51 days', elReduction: 6 }
];
